import { useState, useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import * as vehicleSelectors from '@makemydeal/dr-dash-store/selectors/vehicleSelectors';
import * as dealTemplateSelectors from '@makemydeal/dr-dash-store/selectors/dealTemplateSelectors';
import type { DealTemplate, DealTemplateInventoryType } from '@makemydeal/dr-dash-types';
import { InventoryType } from '../../constants';
import { getInitialInventoryType, normalizeInventoryType } from '../sections/utils';

export type UseTemplateFormState = {
    templateName: string;
    templateDescription: string;
    templateNameConflictError: string | undefined;

    inventoryType: InventoryType;
    make: string;

    inventoryTypes: DealTemplateInventoryType[];
    makes: string[];
    models: string[];
    trims: string[];
    years: string[];

    selectedScopeIds: string[];
};

export type UseTemplateFormActions = {
    setTemplateName: (name: string) => void;
    setTemplateDescription: (description: string) => void;
    setTemplateNameConflictError: (error: string | undefined) => void;

    setInventoryType: (type: InventoryType) => void;

    setInventoryTypes: (types: DealTemplateInventoryType[]) => void;
    setMakes: (makes: string[]) => void;
    setModels: (models: string[]) => void;
    setTrims: (trims: string[]) => void;
    setYears: (years: string[]) => void;

    setSelectedScopeIds: (scopeIds: string[]) => void;

    resetForm: () => void;
    initializeFromTemplate: (template: DealTemplate) => void;
    initializeFromVehicle: () => void;
};

export type UseTemplateFormReturn = {
    formState: UseTemplateFormState;
    formActions: UseTemplateFormActions;
};

const getMakeFromInventoryType = (inventoryType: InventoryType, vehicleMake: string) =>
    (inventoryType === InventoryType.New || inventoryType === InventoryType.CPO) && vehicleMake ? vehicleMake : 'All';

export const useTemplateForm = (): UseTemplateFormReturn => {
    const {
        make: vehicleMake,
        condition: vehicleCondition,
        model: vehicleModel,
        year: vehicleYear,
        trim: vehicleTrim
    } = useSelector(vehicleSelectors.getVehicle);

    const scenarioVehicles = useSelector(dealTemplateSelectors.getScenarioVehicleDetails);
    const allScopeIdsRef = useRef<string[]>([]);
    allScopeIdsRef.current = scenarioVehicles.map((s) => s.scopeId);

    const [templateName, setTemplateName] = useState<string>('');
    const [templateDescription, setTemplateDescription] = useState<string>('');
    const [templateNameConflictError, setTemplateNameConflictError] = useState<string | undefined>();

    const [inventoryType, setInventoryType] = useState<InventoryType>(getInitialInventoryType(vehicleCondition));

    const [inventoryTypes, setInventoryTypes] = useState<DealTemplateInventoryType[]>([]);
    const [makes, setMakes] = useState<string[]>([]);
    const [models, setModels] = useState<string[]>([]);
    const [trims, setTrims] = useState<string[]>([]);
    const [years, setYears] = useState<string[]>([]);

    const [selectedScopeIds, setSelectedScopeIds] = useState<string[]>(() => allScopeIdsRef.current);

    const [make, setMake] = useState(getMakeFromInventoryType(inventoryType, vehicleMake));

    const resetForm = useCallback(() => {
        setTemplateName('');
        setTemplateDescription('');
        setInventoryType(getInitialInventoryType(vehicleCondition));
        setTemplateNameConflictError(undefined);

        setInventoryTypes([]);
        setYears([]);
        setMakes([]);
        setModels([]);
        setTrims([]);

        setSelectedScopeIds(allScopeIdsRef.current);
    }, [vehicleCondition]);

    const initializeFromVehicle = useCallback(() => {
        if (vehicleCondition) {
            setInventoryTypes([normalizeInventoryType(vehicleCondition)]);
        }

        if (vehicleMake) {
            setMakes([vehicleMake]);
        }

        if (vehicleModel) {
            setModels([vehicleModel]);
        }

        if (vehicleYear) {
            setYears([vehicleYear.toString()]);
        }

        if (vehicleTrim) {
            setTrims([vehicleTrim]);
        }
    }, [
        vehicleCondition,
        vehicleMake,
        vehicleModel,
        vehicleYear,
        vehicleTrim,
        setInventoryTypes,
        setMakes,
        setModels,
        setYears,
        setTrims
    ]);

    useEffect(() => {
        resetForm();
    }, [resetForm]);

    useEffect(() => {
        setSelectedScopeIds(allScopeIdsRef.current);
    }, [scenarioVehicles.length]);

    useEffect(() => {
        setMake(getMakeFromInventoryType(inventoryType, vehicleMake));
    }, [inventoryType]);

    const initializeFromTemplate = useCallback(
        (template: DealTemplate) => {
            setTemplateName(template.name || '');
            setTemplateDescription(template.description || '');

            setInventoryTypes(template.inventoryTypes || ['All']);
            setYears(template.years?.map((year) => year.toString()) || ['All']);
            setMakes(template.makes || ['All']);
            setModels(template.models || ['All']);
            setTrims(template.trims || ['All']);

            setTemplateNameConflictError(undefined);
        },
        [
            setTemplateName,
            setTemplateDescription,
            setInventoryTypes,
            setYears,
            setMakes,
            setModels,
            setTrims,
            setTemplateNameConflictError
        ]
    );

    const formState: UseTemplateFormState = {
        templateName,
        templateDescription,
        templateNameConflictError,
        inventoryType,
        make,
        inventoryTypes,
        makes,
        models,
        trims,
        years,
        selectedScopeIds
    };

    const formActions: UseTemplateFormActions = {
        setTemplateName,
        setTemplateDescription,
        setTemplateNameConflictError,
        setInventoryType,
        setInventoryTypes,
        setMakes,
        setModels,
        setTrims,
        setYears,
        setSelectedScopeIds,
        resetForm,
        initializeFromTemplate,
        initializeFromVehicle
    };

    return {
        formState,
        formActions
    };
};
